import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { SearchInput } from '@design-system/components/searchinput';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { sprinkles } from '@design-system/sprinkles.css';
import { isEmpty } from 'lodash';
import {
  BundleContent,
  BundleSectionList,
  BundlesTable,
} from '../viewModel/subscriptionBundle.vm.mapper';
import { TooltipWrapper } from '../../view/TooltipWrapper';
import { StatusTag } from '@client/new/pages/programs/view/components/StatusTag';
import { ProgramReadinessPopover } from '@client/new/pages/programs/view/components/ProgramReadinessPopover';
import { ProgramReadiness } from '@client/new/pages/programs/model/programs.types';
import { SubscriptionBundlesVM } from '../viewModel/subscriptionBundles.viewModel';
import { Button } from '@design-system/components/button/button';
import { Modal } from '@design-system/components/modal/modal';
import { Link } from 'react-router';

export const BundlesContentTable = ({
  list,
}: {
  list: SubscriptionBundlesVM['list'];
}) => {
  if (!list.data) return null;

  return (
    <div className="flex flex-col bg-white rounded-[10px] gap-3 pb-2">
      {!isEmpty(list.cta) && (
        <div className="flex w-full justify-end items-center gap-4 px-6 py-4">
          {list.cta.map((item) => (
            <Button
              key={item.label}
              color="neutral"
              variant={item.variant || 'filled'}
              onClick={item.onClick}
              disabled={item.disabled}
            >
              {item.label}
            </Button>
          ))}
        </div>
      )}
      <TableAccordion header={list.data.header}>
        <ContentSections
          {...list.data}
          searchText={list.searchText}
          search={list.search}
        />
      </TableAccordion>
    </div>
  );
};

export const ContentSections = ({
  programs,
  features,
  content,
  searchText,
  search,
  links,
}: Pick<BundlesTable, 'programs' | 'features' | 'content' | 'links'> & {
  searchText: string;
  search: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="max-w-[300px] px-4">
        <SearchInput
          placeholder="Search by name"
          value={searchText}
          onChange={(e) => search(e.target.value)}
          onClear={() => search('')}
        />
      </div>
      {searchText && isEmpty(features.list) && isEmpty(programs.list) ? (
        <div className="flex flex-col justify-center items-center w-full h-full">
          <div className="flex gap-1">
            <Typography className={sprinkles({ color: 'N-80' })}>
              No results found for:
            </Typography>
            <Typography className="text-black">{searchText}</Typography>
          </div>
          <Typography className={sprinkles({ color: 'N-80' })}>
            Please try searching with another term.
          </Typography>
        </div>
      ) : null}
      <div className="flex flex-col bg-white gap-4">
        {isEmpty(features.list) ? null : (
          <SectionAccordion title="Features">
            <ListWithSelect list={features.list} />
          </SectionAccordion>
        )}
        <SectionAccordion title="Programs">
          {isEmpty(programs.list) ? (
            <div className="flex items-center px-8 gap-1">
              <Link to={links.programsPath}>
                <Typography
                  variant="body"
                  size="medium"
                  color="P-80"
                  className={sprinkles({ textDecoration: 'underline' })}
                >
                  Create your first program
                </Typography>
              </Link>
              <Typography variant="body" size="medium">
                in order to include them in offer
              </Typography>
            </div>
          ) : (
            <ListWithSelect list={programs.list} />
          )}
        </SectionAccordion>
        <SectionAccordion title="Extras">
          {isEmpty(content.list) ? (
            <div className="flex items-center px-8 gap-1">
              <Link to={links.extrasPath}>
                <Typography
                  variant="body"
                  size="medium"
                  color="P-80"
                  className={sprinkles({ textDecoration: 'underline' })}
                >
                  Create your first content
                </Typography>
              </Link>{' '}
              <Typography variant="body" size="medium">
                in order to include them in offer
              </Typography>
            </div>
          ) : (
            <ListWithSelect list={content.list} />
          )}
        </SectionAccordion>
      </div>
    </div>
  );
};

const TableAccordion: React.FC<
  React.PropsWithChildren<{ header: BundlesTable['header'] }>
> = ({ header: { bundleName, section }, children }) => (
  <div className="flex flex-col gap-4 px-2 w-full">
    <div
      className="flex gap-4 items-center justify-between rounded-md px-[25px] py-1 w-full h-[52px] cursor-pointer"
      onClick={section.toggle}
      data-testid="subscription-content-header"
    >
      <div className="flex items-center gap-2">
        <FontAwesomeIcon
          icon={section.open ? faChevronUp : faChevronDown}
          className={sprinkles({ color: 'N-80' })}
        />
        <Typography variant="body" size="medium">
          {section.open ? 'Close' : 'Open'}
        </Typography>
      </div>
      <div className="flex gap-4 md:gap-16 pr-2">
        {bundleName.map((name) => (
          <Typography
            key={name}
            className="w-[80px] md:w-[180px] text-wrap"
            variant="body"
            size="large"
          >
            {name}
          </Typography>
        ))}
      </div>
    </div>
    {section.open && children}
  </div>
);

const SectionAccordion: React.FC<
  React.PropsWithChildren<{ title: string }>
> = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex flex-col gap-4 px-2">
      <div
        className="flex gap-4 items-center hover:bg-[#EEE] rounded-md px-[25px] py-1"
        onClick={() => setOpen((prev) => !prev)}
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronUp}
          className={sprinkles({ color: 'N-80' })}
        />
        <Typography
          variant="body"
          size="small"
          className={sprinkles({ color: 'N-80' })}
        >
          {title}
        </Typography>
        <hr className="w-full" />
      </div>
      {open && children}
    </div>
  );
};

type PendingConfirmation = {
  title: string;
  description: string;
  onConfirm: () => void;
};

const ListWithSelect = ({ list }: { list: BundleSectionList }) => {
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverReadiness, setPopoverReadiness] =
    useState<ProgramReadiness | null>(null);

  const handleChange = (b: BundleContent) => {
    if (b.checked && b.offConfirmation) {
      setPendingConfirmation({ ...b.offConfirmation, onConfirm: b.onChange });
    } else {
      b.onChange();
    }
  };

  const openPopover = (anchor: HTMLElement, readiness: ProgramReadiness) => {
    setPopoverAnchor(anchor);
    setPopoverReadiness(readiness);
  };
  const closePopover = () => setPopoverAnchor(null);

  return (
    <>
      {pendingConfirmation && (
        <Modal
          isOpen
          onClose={() => setPendingConfirmation(null)}
          title={pendingConfirmation.title}
          modalClassName="w-full md:w-[500px]"
        >
          <div className="flex flex-col gap-6">
            <Typography variant="body" size="large" fontWeight="normal">
              {pendingConfirmation.description}
            </Typography>
            <div className="flex gap-4 justify-center">
              <Button
                variant="text"
                color="neutral"
                onClick={() => setPendingConfirmation(null)}
                className="w-[180px]"
              >
                Go back
              </Button>
              <Button
                color="neutral"
                onClick={() => {
                  pendingConfirmation.onConfirm();
                  setPendingConfirmation(null);
                }}
                className="w-[180px]"
              >
                Yes, turn off
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex flex-col gap-4">
        {(() => {
          // Render a dedicated status column only when the section has any
          // status-bearing row (Programs). Features/Extras keep the original
          // two-column layout so their rows don't acquire empty whitespace.
          const sectionHasStatus = list.some((r) => !!r.status);
          return list.map((row) => (
            <div
              key={row.name}
              className="flex justify-between items-center px-8 py-1 hover:bg-[#F8F8F8] rounded-md"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Typography
                  variant="body"
                  size="medium"
                  className="max-w-[100px] md:max-w-full overflow-x-clip md:overflow-x-none flex-1"
                >
                  {row.name}
                </Typography>
                {sectionHasStatus ? (
                  // Fixed-width slot so pills align vertically down the
                  // Programs section regardless of label length.
                  <div className="w-[140px] flex items-center flex-shrink-0">
                    {row.status ? (
                      <StatusTag
                        state={row.status.state}
                        readiness={row.status.readiness}
                        onReadinessClick={
                          row.status.readiness
                            ? (anchor) =>
                                openPopover(anchor, row.status!.readiness!)
                            : undefined
                        }
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-4 md:gap-16">
                {row.bundleContent.map((b, i) => (
                  <div
                    key={i}
                    className="w-[80px] md:w-[180px] flex justify-center"
                  >
                    <TooltipWrapper
                      tooltip={b.disabled ? b.tooltip : undefined}
                    >
                      <Checkbox
                        checkboxVariant="neutral"
                        checked={b.checked}
                        onChange={() => handleChange(b)}
                        disabled={b.disabled}
                      />
                    </TooltipWrapper>
                  </div>
                ))}
              </div>
            </div>
          ));
        })()}
      </div>
      <ProgramReadinessPopover
        anchor={popoverAnchor}
        readiness={popoverReadiness}
        onClose={closePopover}
      />
    </>
  );
};
